import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import api, { resolveMediaUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Play, ImageIcon } from "lucide-react";

const DAY_TR = { Monday: "Pazartesi", Tuesday: "Salı", Wednesday: "Çarşamba", Thursday: "Perşembe", Friday: "Cuma", Saturday: "Cumartesi", Sunday: "Pazar" };
const DAY_AR = { Monday: "الاثنين", Tuesday: "الثلاثاء", Wednesday: "الأربعاء", Thursday: "الخميس", Friday: "الجمعة", Saturday: "السبت", Sunday: "الأحد" };
const DAY_RU = { Monday: "Понедельник", Tuesday: "Вторник", Wednesday: "Среда", Thursday: "Четверг", Friday: "Пятница", Saturday: "Суббота", Sunday: "Воскресенье" };

export default function Menu() {
  const { t, tt, lang } = useLang();
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/menus/current").then((r) => setCurrent(r.data)).catch(() => {});
  }, []);

  const localDay = (d) => {
    if (lang === "tr") return DAY_TR[d];
    if (lang === "ar") return DAY_AR[d];
    if (lang === "ru") return DAY_RU[d];
    return d;
  };

  return (
    <div>
      <div className="section-warm py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-3">{t("nav.menu")}</div>
          <h1 className="font-serif text-5xl md:text-6xl text-brand-navy" data-testid="menu-title">{t("sections.menu_title")}</h1>
          <p className="mt-3 text-slate-700 max-w-2xl">{t("sections.menu_desc")}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      {current ? (
        <div className="mt-2" data-testid="menu-weekly-view">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-3xl text-brand-navy">{tt(current.label)}</h2>
            <div className="text-sm text-slate-500">{current.start_date} → {current.end_date}</div>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {current.meals.map((m) => (
              <button
                key={m.day}
                onClick={() => setSelected(m)}
                className="group text-start bg-white border border-brand-gold/20 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition"
                data-testid={`menu-day-${m.day}`}
              >
                {(m.image_url || m.video_url) && (
                  <div className="relative aspect-[16/9] overflow-hidden bg-brand-navy/5">
                    {m.image_url && (
                      <img src={resolveMediaUrl(m.image_url)} alt={localDay(m.day)} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                    )}
                    {m.video_url && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                        <div className="bg-white/90 rounded-full p-3">
                          <Play size={22} className="text-brand-orange" fill="currentColor" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-6">
                  <div className="font-serif text-2xl text-brand-navy">{localDay(m.day)}</div>
                  <div className="mt-4 space-y-3 text-sm">
                    <MealLine label={t("sections.breakfast")} value={tt(m.breakfast)} />
                    <MealLine label={t("sections.lunch")} value={tt(m.lunch)} />
                    <MealLine label={t("sections.dinner")} value={tt(m.dinner)} />
                  </div>
                  <div className="mt-4 text-xs text-brand-orange">
                    {m.video_url ? t("cta.watch_video") : t("cta.view_details")} →
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-8 text-slate-500">—</div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl bg-canvas-cream" data-testid="menu-day-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl text-brand-navy">
                  {localDay(selected.day)}
                </DialogTitle>
              </DialogHeader>
              {selected.video_url ? (
                <video
                  src={resolveMediaUrl(selected.video_url)}
                  controls
                  autoPlay
                  className="w-full rounded-xl bg-black"
                />
              ) : selected.image_url ? (
                <img src={resolveMediaUrl(selected.image_url)} alt="" className="w-full rounded-xl" />
              ) : (
                <div className="flex items-center gap-2 text-slate-500 text-sm"><ImageIcon size={16}/> {t("cta.no_media")}</div>
              )}
              <div className="grid gap-3 mt-2">
                <MealLine label={t("sections.breakfast")} value={tt(selected.breakfast)} />
                <MealLine label={t("sections.lunch")} value={tt(selected.lunch)} />
                <MealLine label={t("sections.dinner")} value={tt(selected.dinner)} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

function MealLine({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-brand-orange font-semibold">{label}</div>
      <div className="text-slate-800">{value}</div>
    </div>
  );
}
